/**
 * イベントメタオブジェクト JSON の全ページ読み込み
 *
 * snippets/events-data-script.liquid が paginate（250件/ページ）で出力した
 * application/json を起点に、2ページ目以降を Section Rendering API で取得して結合する。
 *
 * 読み込み順（必須）:
 *   1. snippets/events-loader-bootstrap.liquid … 同期スタブ + 本ファイルを defer
 *   2. セクションのインラインJS … enhanceWhenReady を呼ぶ（スタブならキューへ）
 *   3. 本ファイル実行 … 実処理を登録しキューをフラッシュ
 *
 * 使い方:
 *   window.SalomonEvents.enhanceWhenReady(dataNode, (merged) => {
 *     allEvents = merged;
 *     reloadItems();
 *   });
 *
 * 注意: bootstrap は各イベントセクションから render されるため、同一ページで
 * 本スクリプトが複数回実行され得る。IIFE + _loaderReady ガードで冪等にしている。
 */
(() => {
  window.SalomonEvents = window.SalomonEvents || {};
  window.SalomonEvents._enhanceQueue = window.SalomonEvents._enhanceQueue || [];
  if (window.SalomonEvents._loaderReady) return;

  /**
   * dataNode（1ページ目 JSON）から全ページ分を結合して返す。
   * 失敗ページはスキップし、最終件数が data-events-total と不一致なら console.warn する。
   */
  window.SalomonEvents.loadAllFromJsonNode = async function loadAllFromJsonNode(dataNode) {
    if (!dataNode) return [];

    let events = [];
    try {
      events = JSON.parse(dataNode.textContent || "[]");
    } catch (e) {
      console.warn("[SalomonEvents] 1ページ目 JSON のパースに失敗しました", e);
      events = [];
    }
    if (!Array.isArray(events)) events = [];

    const expectedTotal = parseInt(dataNode.dataset.eventsTotal || "0", 10);
    const totalPages = parseInt(dataNode.dataset.eventsPages || "1", 10);
    const sectionId = dataNode.dataset.sectionId || "";
    if (totalPages <= 1 || !sectionId) return events;

    const basePath = window.location.pathname;
    const failedPages = [];

    for (let page = 2; page <= totalPages; page++) {
      try {
        const params = new URLSearchParams({
          sections: sectionId,
          page: String(page)
        });
        const res = await fetch(`${basePath}?${params.toString()}`, {
          credentials: "same-origin"
        });
        if (!res.ok) {
          failedPages.push({ page, reason: `HTTP ${res.status}` });
          continue;
        }

        const json = await res.json();
        // Section Rendering API は section.id または sections/ 付きキーで返すことがある
        const html = json[sectionId] || json[`sections/${sectionId}`];
        if (!html) {
          failedPages.push({
            page,
            reason: `レスポンスに section キーなし (tried: ${sectionId})`,
            keys: Object.keys(json)
          });
          continue;
        }

        const doc = new DOMParser().parseFromString(html, "text/html");
        const pageNode = doc.getElementById(dataNode.id);
        if (!pageNode?.textContent) {
          failedPages.push({ page, reason: `script#${dataNode.id} が見つからない` });
          continue;
        }

        const chunk = JSON.parse(pageNode.textContent);
        if (Array.isArray(chunk)) {
          events = events.concat(chunk);
        } else {
          failedPages.push({ page, reason: "JSON が配列ではない" });
        }
      } catch (e) {
        failedPages.push({ page, reason: e?.message || String(e) });
      }
    }

    if (failedPages.length > 0) {
      console.warn(
        "[SalomonEvents] 追加ページの取得に失敗しました。先頭ページ分のみ表示されます。",
        { sectionId, totalPages, expectedTotal, loaded: events.length, failedPages }
      );
    }

    if (expectedTotal > 0 && events.length !== expectedTotal) {
      console.warn(
        "[SalomonEvents] 結合後の件数と data-events-total が一致しません。",
        { expectedTotal, loaded: events.length, totalPages, sectionId, failedPages }
      );
    }

    return events;
  };

  /**
   * 251件超のときだけ残りページを取得し、件数が増えたときコールバックする。
   * pages <= 1 の場合は何もしない（通常のイベント件数では追加コストなし）。
   *
   * @param {HTMLElement|null} dataNode
   * @param {(merged: Array) => void} onMerged
   */
  window.SalomonEvents.enhanceWhenReady = function enhanceWhenReady(dataNode, onMerged) {
    if (!dataNode || typeof onMerged !== "function") return;

    const totalPages = parseInt(dataNode.dataset.eventsPages || "1", 10);
    if (totalPages <= 1) return;

    (async () => {
      const firstPageCount = (() => {
        try {
          const parsed = JSON.parse(dataNode.textContent || "[]");
          return Array.isArray(parsed) ? parsed.length : 0;
        } catch (e) {
          return 0;
        }
      })();

      const merged = await window.SalomonEvents.loadAllFromJsonNode(dataNode);
      if (Array.isArray(merged) && merged.length > firstPageCount) {
        onMerged(merged);
      }
    })();
  };

  window.SalomonEvents._loaderReady = true;

  // bootstrap スタブが積んだキューをフラッシュ
  window.SalomonEvents._enhanceQueue.splice(0).forEach(([dataNode, onMerged]) => {
    window.SalomonEvents.enhanceWhenReady(dataNode, onMerged);
  });
})();
